import React from 'react';
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { LineChart, Line, ResponsiveContainer, YAxis } from 'recharts';
import Grid from '@mui/material/Grid2';
import { getAssessmentSeverityLabel } from './dashboardDisplayUtils';
import { DASHBOARD_COLORS, clickableTileSx, sectionCardSx, sectionTitleSx, trendHintSx } from './dashboardStyles';

// Shared presentation only. The live container supplies its existing handlers;
// onboarding supplies example data and inert/no-op handlers. No data hooks here.
// Lower PHQ-9 / GAD-7 scores are better.
const trendOf = (latest, prev) => {
  if (!prev || latest?.total_score == null || prev.total_score == null) return null;
  if (latest.total_score < prev.total_score) return { label: 'Improving', bg: '#E6F7F5', fg: '#0F766E' };
  if (latest.total_score > prev.total_score) return { label: 'Worsening', bg: '#FFF7ED', fg: '#B45309' };
  return { label: 'Stable', bg: '#F1F4F7', fg: '#374151' };
};

export default function DashboardAssessmentCards({ compact = false,  assessmentGroups, assessmentsLoading, assessmentsRef, formatDate, getAssessmentMeta, getSeverityColor, setSelectedAssessmentType }) {
  return (<Box
        data-tour="demo-dashboard-assessments"
        ref={assessmentsRef}
        sx={{ ...sectionCardSx, mt: compact ? 0 : 4, height: compact ? '100%' : undefined }}
        >
        <Typography variant="h6" sx={{ ...sectionTitleSx, mb: 2 }}>Mental Health Assessments</Typography>

        {assessmentsLoading ? (
             <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} sx={{ color: DASHBOARD_COLORS.accent }} />
             </Box>
        ) : Object.keys(assessmentGroups).length === 0 ? (
             <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No assessments completed yet.
             </Typography>
        ) : (
            <Grid container spacing={2}>
                {Object.entries(assessmentGroups).map(([type, history]) => {
                    const latest = history[0];
                    const prev = history[1];
                    const meta = getAssessmentMeta(type);
                    const severityLabel = getAssessmentSeverityLabel(type, latest);
                    
                    return (
                        <Grid size={{ xs: 12, sm: 6 }} key={type}>
                            <Box
                                role="button"
                                tabIndex={0}
                                aria-label={`Show ${meta.label} history`}
                                onClick={() => setSelectedAssessmentType(type)}
                                onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelectedAssessmentType(type); } }}
                                sx={{ ...clickableTileSx, p: 2 }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Box>
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: DASHBOARD_COLORS.text }}>
                                            {meta.label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(latest.completed_at)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography
                                            variant="h5"
                                            sx={{ fontWeight: 800, color: getSeverityColor(severityLabel), lineHeight: 1 }}
                                        >
                                            {latest.total_score}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                                            {severityLabel}
                                        </Typography>
                                    </Box>
                                </Stack>
                                {trendOf(latest, prev) && (
                                    <Chip label={trendOf(latest, prev).label} size="small" sx={{ mt: 1, height: 20, fontSize: 11, fontWeight: 600, bgcolor: trendOf(latest, prev).bg, color: trendOf(latest, prev).fg }} />
                                )}
                                {history.length > 1 && (
                                    <Box sx={{ height: 40, mt: 1 }} aria-hidden="true">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={[...history].reverse().map((h) => ({ score: h.total_score }))} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                                                <YAxis hide domain={[0, meta.max || 'dataMax']} />
                                                <Line type="monotone" dataKey="score" stroke="#4F46E5" strokeWidth={2.5} dot={false} isAnimationActive={false} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Box>
                                )}
                                <Typography variant="caption" sx={{ display: 'block', color: DASHBOARD_COLORS.muted, mt: 0.5 }}>
                                    {[...history].slice(0, 4).reverse().map((h) => h.total_score).join(' → ')} · last done {formatDate(latest.completed_at)}
                                </Typography>
                                <Typography sx={{ ...trendHintSx, mt: 1 }}>View history ›</Typography>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
        )}
        </Box>);
}
